import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { errorMessage, generateXdomeSbom, type XDomeSbomResponse } from '../../lib/api';

export default function XDomeSbomCard(): ReactNode {
  const { t } = useLingui();
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('1.7');
  const [demoData, setDemoData] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<XDomeSbomResponse | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!result) {
      setDownloadUrl(null);
      return;
    }
    const url = URL.createObjectURL(new Blob([result.sbom], { type: 'application/vnd.cyclonedx+json' }));
    setDownloadUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [result]);

  async function generate(): Promise<void> {
    if (!file || busy) return;
    setError(null);
    setResult(null);
    if (file.size > 10 * 1024 * 1024) {
      setError(t`Die CSV darf maximal 10 MiB groß sein.`);
      return;
    }
    setBusy(true);
    try {
      setResult(await generateXdomeSbom(file, version, demoData));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const deviceCount = result?.device_count ?? 0;
  const osCount = result?.os_count ?? 0;

  return (
    <div className="card">
      <div className="card-head"><h2><Trans>SBOM aus xDome-CSV erzeugen</Trans></h2></div>
      <div className="card-body">
        <p>
          <Trans>
            Wandelt einen Claroty-xDome-Geräteexport in eine CycloneDX-SBOM um. Erkannte
            Betriebssysteme werden separat erfasst.
          </Trans>
        </p>
        <label>
          <input type="checkbox" checked={demoData} disabled={busy} onChange={(event) => {
            setDemoData(event.target.checked);
            setResult(null);
            setError(null);
          }} />{' '}
          <Trans>Demo-Maschinen mit Komponenten, Kunden und Auslieferungen erzeugen</Trans>
        </label>
        {demoData ? <p className="muted">
          <Trans>
            Pro Standort entsteht ein Demo-Kunde; je bis zu fünf Geräte bilden eine Maschine mit eigener
            Stückliste, Produktversion „Demo“ und Demo-Seriennummer. Fehlende Komponentenversionen
            erscheinen als „Nicht angegeben“, Hersteller aus der CSV bleiben erhalten. Übernehmen Sie die
            SBOM anschließend über „SBOM importieren“.
          </Trans>
        </p> : null}
        <div className="toolbar">
          <label>
            <Trans>xDome-CSV (UTF-8, maximal 10 MiB)</Trans>{' '}
            <input type="file" accept=".csv,text/csv" disabled={busy}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setResult(null);
                setError(null);
              }} />
          </label>
          <label>
            <Trans>CycloneDX-Version</Trans>{' '}
            <select value={version} disabled={busy} onChange={(event) => {
              setVersion(event.target.value);
              setResult(null);
              setError(null);
            }}>
              <option value="1.7">1.7</option>
              <option value="1.6">1.6</option>
            </select>
          </label>
          <button type="button" disabled={!file || busy} onClick={() => void generate()}>
            {busy ? t`SBOM wird erzeugt…` : t`SBOM erzeugen`}
          </button>
        </div>
        {error ? <Banner kind="error">{error}</Banner> : null}
        <div aria-live="polite">
          {result ? (
            <p>
              {t`${plural(deviceCount, { one: '# Gerät', other: '# Geräte' })} und ${plural(osCount, { one: '# Betriebssystem', other: '# Betriebssysteme' })} erfasst.`}{' '}
              {downloadUrl ? <a href={downloadUrl} download={result.filename}><Trans>SBOM herunterladen</Trans></a> : null}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
